import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";
import { CONSTANT_KEYS } from "../constants";
import { Formio } from "formiojs";

//Views
import Login from "@/views/Login.vue";

//Components
Vue.use(VueRouter);
const baseTitle = "API Connect: University Platform";
const currentNavigation = performance.getEntriesByType("navigation");
const validNavigationTypes = ["reload", "navigate"];
const validDeprecatedNavigationTypes = [0, 1];

//maintanance mode route
let routes = [
  {
    path: "*",
    name: "Maintanance",
    component: () => import("../views/Maintenance.vue"),
    meta: {
      requiresLogin: false,
      title: "test",
    },
  },
];
if (
  !process.env.VUE_APP_MAINTENANCE_MODE ||
  process.env.VUE_APP_MAINTENANCE_MODE.toLowerCase() !== "true"
) {
  routes = [
    {
      path: "/",
      redirect: "/sign-in",
      meta: {
        requiresLogin: false,
        title: "Sign in",
      },
    },
    {
      path: "/my-students",
      name: "My Students",
      component: () =>
        import(/* webpackChunkName: "my-students" */ "../views/MyStudents.vue"),
      meta: {
        requiresLogin: true,
        title: "My Students",
      },
    },
    {
      path: "/student-detail/:id/:tab",
      name: "Student Details",
      component: () =>
        import(
          /* webpackChunkName: "student-detail" */ "../views/StudentDetail.vue"
        ),
      meta: {
        requiresLogin: true,
        title: "Student details",
      },
      props: true,
    },
    {
      path: "/notification-settings",
      name: "Notification Settings",
      component: () =>
        import(
          /* webpackChunkName: "notifications-settings" */ "../views/NotificationSettings.vue"
        ),
      meta: {
        requiresLogin: true,
        title: "Notification Settings",
      },
    },
    {
      path: "/404-not-found",
      name: "404",
      component: () => import("../views/404Page.vue"),
      meta: {
        requiresLogin: false,
        title: "404",
      },
    },
    {
      path: "/sign-in",
      name: "SignIn",
      component: Login,
      meta: {
        requiresLogin: false,
        title: "Sign In",
      },
    },
    {
      path: "/forgot-password",
      name: "Forgot Password",
      component: Login,
      meta: {
        requiresLogin: false,
        title: "Forgot Password",
      },
    },
    {
      path: "/set-password",
      name: "Set Password",
      component: Login,
      meta: {
        requiresLogin: false,
        title: "Set Password",
      },
    },
    {
      path: "/reset-password",
      name: "Reset Password",
      component: Login,
      meta: {
        requiresLogin: false,
        title: "Reset Password",
      },
    },
  ];
}

const router = new VueRouter({
  mode: "history",
  routes,
});

router.beforeEach(async function(to, from, next) {
  store.commit("closeBothMenus");
  let userLoggedIn = false;
  let routeRequiresAuth = to.meta.requiresLogin;
  let routeExists = to.matched.length > 0;
  let pendingPasswordChange =
    localStorage.getItem(CONSTANT_KEYS.PASSWORD_STORAGE_KEY) !== null;

  //Validate token
  if (localStorage.getItem("formioToken") && !pendingPasswordChange) {
    const formioURL = process.env.VUE_APP_FORMIO_URL + "/current";

    let response = await fetch(formioURL, {
      headers: { "x-jwt-token": localStorage.getItem("formioToken") }
    });

    if (response.status === 200) {
      setPageProps();
      userLoggedIn = true;
    }
  }

  if (!routeExists) {
    // Route does not exists
    next({ name: "404" });
  } else if (pendingPasswordChange && to.path !== "/reset-password") {
    // User has pending password change
    next({ name: "Reset Password" });
  } else if (
    (routeRequiresAuth && userLoggedIn) ||
    (!routeRequiresAuth && !userLoggedIn)
  ) {
    // Route needs auth & user Authenticated & viceversa
    next();
  } else if (routeRequiresAuth && !userLoggedIn) {
    // Route needs auth but user isn't authenticated
    Formio.logout();
    store.dispatch("resetCounts");
    store.commit("logout");
    next({ name: "SignIn" });
  } else if (!routeRequiresAuth && userLoggedIn) {
    // Route does not need auth but user is
    if (to.path === "/sign-in") {
      // Stops login form
      next({ name: "My Students" });
    } else {
      next();
    }
  }
  document.title = baseTitle + " - " + to.meta.title;
});

const setPageProps = () => {
  store.commit("login", {
    user: JSON.parse(localStorage.getItem("formioUser")),
    token: localStorage.getItem("formioToken"),
  });

  store.commit(
    "setUniversity",
    JSON.parse(localStorage.getItem("formioUniversity"))
  );

  store.commit(
    "setUserDetails",
    JSON.parse(localStorage.getItem("userDetails"))
  );

  store.commit(
    "setTabProgramSessionId",
    JSON.parse(localStorage.getItem("tabProgramSessionId"))
  );

  // Checks if user navigated or reloaded the page
  guardNavigationReloads();
};

export default router;

function guardNavigationReloads() {
  if (
    (guardNavigationAPILevel2() &&
      validNavigationTypes.includes(currentNavigation[0]["type"])) ||
    (guardDeprecatedAPI() &&
      validDeprecatedNavigationTypes.includes(performance.navigation.type))
  ) {
    reloadTabData();
  }
}

function guardNavigationAPILevel2() {
  // Check if the browser will work with Navigation Timing API Level 2
  if (
    typeof performance !== "object" ||
    typeof performance.getEntriesByType !== "function" ||
    performance.getEntriesByType("navigation").length < 1
  ) {
    return false;
  }
  return true;
}

function guardDeprecatedAPI() {
  // Check if deprecated navigation API could work
  if (
    typeof performance !== "object" ||
    typeof performance.navigation !== "object" ||
    typeof performance.navigation.type !== "number"
  ) {
    return false;
  }
  return true;
}

function reloadTabData() {
  localStorage.getItem("tabIndex")
    ? store.commit("setCurrentTab", {
        index: localStorage.getItem("tabIndex"),
        clear: false,
      })
    : "";

  localStorage.getItem("tabUrl")
    ? store.commit("setLastViewedPage", localStorage.getItem("tabUrl"))
    : "";

  localStorage.getItem("tabInput")
    ? store.commit("setLastSearchInput", localStorage.getItem("tabInput"))
    : "";

  localStorage.getItem("prospectiveType")
    ? store.commit(
        "setProspectiveType",
        localStorage.getItem("prospectiveType")
      )
    : "";

  localStorage.getItem("prospectiveTimeframe")
    ? store.commit(
        "setProspectiveTimeframe",
        localStorage.getItem("prospectiveTimeframe")
      )
    : "";

  localStorage.getItem("prospectiveMajor")
    ? store.commit(
        "setProspectiveMajor",
        localStorage.getItem("prospectiveMajor")
      )
    : "";

  localStorage.getItem("prospectiveFrom")
    ? store.commit(
        "setProspectiveFrom",
        localStorage.getItem("prospectiveFrom")
      )
    : "";

  localStorage.getItem("prospectiveTo")
    ? store.commit("setProspectiveTo", localStorage.getItem("prospectiveTo"))
    : "";

  localStorage.getItem("tabsFilters")
    ? store.commit(
        "setLastFilters",
        JSON.parse(localStorage.getItem("tabsFilters"))
      )
    : "";

  localStorage.getItem("application_id")
    ? store.commit("setApplicationId", localStorage.getItem("application_id"))
    : "";

  localStorage.getItem("tabOrders")
    ? store.commit("setOrder", JSON.parse(localStorage.getItem("tabOrders")))
    : "";
}
